import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MODES, ROOT_TEAM_ID, SORT_DIRECTION, type SortDirection } from '@scio3/shared-types';
import { selectIsAllTeamsScope, useTeamStore } from '@/stores/team-store';
import { useTeamNoun } from '@/hooks/useTeamNoun';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';

/**
 * All view (Group Security spec §1 · tasks T902/T904) — the list-surface side of the
 * cross-teamspace scope.
 *
 * The backend read path already returns each user's FULL cross-team reach and treats
 * `filters.teamId` as an optional filter (spec §1a), so nothing here fetches: a list in All scope
 * is showing rows that were always in the response, and these helpers only decide how to LABEL
 * them (Workspace column) and what to refuse (create).
 *
 * Everything behind `SHOW_ALL_VIEW` — flip that one constant off and every consumer collapses to
 * the pre-All-view behaviour.
 */

/**
 * True while the user has deliberately switched the picker to All (AV-1: the default after login
 * is the current teamspace, never All).
 *
 * `selectIsAllTeamsScope` (team store, T901) already ANDs in `SHOW_ALL_VIEW` and the reach test,
 * so a single-teamspace account can never be in All scope and every consumer below is inert for
 * them without repeating either condition.
 *
 * #2452 — ALSO false for the LEGACY cohort, and this is not belt-and-braces. The All scope is
 * persisted (`team-store`, `persist({ name: 'scio3-team' })`, no partialize), so a legacy account
 * that selected All while the chooser was still shown to it keeps `teamFilter === ALL_TEAMS_SCOPE`
 * in localStorage — with no UI left to clear it, because the chooser is now cohort-gated. The
 * stuck state is visible: the Workspace column appears on every content list
 * (`showWorkspaceColumn = isAllTeamScope`) while the legacy read filter normalizes `'all'` away
 * and returns one teamspace's rows. Reading the cohort here clears it for every consumer at once.
 */
export function useIsAllTeamScope(): boolean {
  const cohort = useGroupSecurityCohort();
  return useTeamStore(selectIsAllTeamsScope) && cohort;
}

/**
 * `teamId` → display name, from the teams list the app already loaded at auth (ME_QUERY). No
 * query is added — an unresolvable id (a teamspace the user just lost, or a row from outside the
 * loaded list) renders as an em dash rather than a raw id.
 *
 * ROOT/Default is relabelled exactly like the TeamSwitcher does: the backend name for the root
 * teamspace is the sentinel string 'Default', never a display string.
 */
export function useTeamNameResolver(): (teamId?: string | null) => string {
  const { t } = useTranslation();
  const { noun } = useTeamNoun();
  const teams = useTeamStore((s) => s.teams);

  const byId = useMemo(() => {
    const m = new Map<string, string>();
    for (const team of teams) {
      m.set(team._id, team.name === 'Default' ? t('Main {{noun}}', { noun }) : team.name);
    }
    return m;
  }, [teams, t, noun]);

  return useCallback(
    (teamId?: string | null) => {
      // A row with no teamId belongs to the root teamspace (the backend omits it there).
      const id = teamId ?? ROOT_TEAM_ID;
      return byId.get(id) ?? '';
    },
    [byId],
  );
}

interface WorkspaceColumnState {
  /** Render the Workspace column / badge? Only in All scope. */
  isAllScope: boolean;
  /** `teamId` → display name (empty string when it can't be resolved). */
  resolveName: (teamId?: string | null) => string;
  /** Column header label — "Workspace" or "Team" per the account's `workspaceIsolation` relabel. */
  label: string;
  /** Active client-side sort direction, or null while the column isn't the sort. */
  sortDirection: SortDirection | null;
  /** Header click — off → ASC → DESC → off. */
  toggleSort: () => void;
  /**
   * Client-side sort by resolved workspace name. Returns the input untouched unless the column
   * is BOTH visible and the active sort, so a surface can call it unconditionally.
   *
   * Client-side per spec §1c.2 — it re-orders the rows already loaded, not the server query, so
   * on a scroll-paginated surface it orders the loaded page (the backend has no workspace-name
   * sort field; adding one is a separate backend change).
   */
  sortRows: <T extends { teamId?: string | null }>(rows: T[]) => T[];
}

/**
 * Everything a list surface needs for the Workspace column (T902). One hook so all 5 surfaces
 * behave identically and the flag is read in exactly one place.
 */
export function useWorkspaceColumn(): WorkspaceColumnState {
  const isAllScope = useIsAllTeamScope();
  const resolveName = useTeamNameResolver();
  const { noun } = useTeamNoun();
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

  const toggleSort = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? SORT_DIRECTION.ASC : prev === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : null,
    );
  }, []);

  const sortRows = useCallback(
    <T extends { teamId?: string | null }>(rows: T[]): T[] => {
      if (!isAllScope || sortDirection === null) return rows;
      const factor = sortDirection === SORT_DIRECTION.ASC ? 1 : -1;
      // Stable: the server sort survives inside each workspace group.
      return rows
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
          const diff = resolveName(a.row.teamId).localeCompare(resolveName(b.row.teamId));
          return diff !== 0 ? diff * factor : a.index - b.index;
        })
        .map(({ row }) => row);
    },
    [isAllScope, sortDirection, resolveName],
  );

  return { isAllScope, resolveName, label: noun, sortDirection, toggleSort, sortRows };
}

interface AllScopeCreateBlock {
  /** Disable every create affordance on this surface. */
  isCreateBlocked: boolean;
  /** `disabledTooltip` text explaining why — undefined when nothing is blocked. */
  createBlockedReason: string | undefined;
}

/**
 * Is this URL `?path=` value "root-like" — i.e. NOT a real folder that carries a `teamId`?
 *
 * True for the Home root (`'/'`, or no param at all) AND for the virtual-location SENTINELS the
 * surfaces write into `?path=` (`SHARE_WITH_ME` / `FAVORITES`): those list rows from elsewhere and
 * have no folder to create into, so they must never be mistaken for a folder.
 *
 * For a surface that already derives its own `isVirtualLocation` from a store mode, OR that flag
 * into the `atRoot` argument instead — this helper is for surfaces whose only signal is the path.
 */
export function isRootLikePath(path?: string | null): boolean {
  return !path || path === '/' || path === MODES.SHARE_WITH_ME || path === MODES.FAVORITES;
}

/**
 * Create is blocked while the scope is All **and only at Home/root** (T904 / spec §1d AV-2, v1).
 *
 * All scope makes the create DESTINATION ambiguous only at root: there, no folder names a
 * teamspace, so a create has nowhere unambiguous to land and All names none. v1 blocks with a
 * tooltip; it deliberately does NOT offer a team picker, and cross-workspace referencing (a
 * playlist pulling foreign assets) is out of scope — it collides with the denormalized
 * playlist-item snapshots.
 *
 * INSIDE a folder there is no ambiguity: the folder carries its own `teamId`, so the destination is
 * fully determined and this gate must contribute NOTHING. The decision there belongs entirely to
 * the surface's existing per-folder check — `usePermissions({ folder }).canCreate(entity)` — which
 * already admits Owner / Super-Admin / Admin / Team-Owner by bypass and evaluates folder security
 * for everyone else. This hook must never be the thing that blocks an in-folder create; an earlier
 * path-blind version was, and it silently shadowed that check.
 *
 * @param atRoot Is the create destination the Home/root level of this surface (no folder)? Virtual
 *   locations (Shared with me / Favorites / Recent) have no folder to create into either — pass
 *   `true` for them, which is also the SAFE default whenever a surface cannot know its path.
 *
 * Compose with the surface's existing gates rather than replacing them:
 *   `disabled={!canCreateX || isVirtualLocation || isCreateBlocked}`
 *   `disabledTooltip={createBlockedReason ?? createDisabledReason}`
 */
export function useAllScopeCreateBlock(atRoot: boolean): AllScopeCreateBlock {
  const { t } = useTranslation();
  const isAllScope = useIsAllTeamScope();
  const isCreateBlocked = isAllScope && atRoot;

  return {
    isCreateBlocked,
    createBlockedReason: isCreateBlocked ? t('Select a teamspace to create') : undefined,
  };
}
